import Vue from 'vue';
import './pollyfills';
import VueRouter from 'vue-router';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import VueRouterPrefetch from 'vue-router-prefetch';
import VueNotify from 'vue-notifyjs';
import VeeValidate from 'vee-validate';
import locale from 'element-ui/lib/locale/lang/en';
import axios from 'axios';
import ElementUI from 'element-ui';
import VueMoment from 'vue-moment';
// import makeStore from './store';
import VueAnalytics from 'vue-analytics';
import vSelect from 'vue-select';
import mixins from './mixins';
import App from './App.vue';
import store from './store';
import Mgr from './services/SecurityService';
import apiService from './repositories/apiService.js';
import 'vue-select/dist/vue-select.css';

// Plugins
import GlobalComponents from './globalComponents';
import GlobalDirectives from './globalDirectives';
import SideBar from './components/UIComponents/SidebarPlugin';
import initProgress from './progressbar';

// router setup
import routes from './routes/routes';

// library imports
import './assets/sass/paper-dashboard.scss';
import './assets/sass/demo.scss';
import './assets/sass/reckoning.scss';
import './assets/sass/editor.scss';

import 'font-awesome/css/font-awesome.min.css';

import sidebarLinks from './sidebarLinks';
import './registerServiceWorker';

// Service Container
import ServiceContainer from './services/serviceContainer';

Bugsnag.start({
    apiKey: process.env.VUE_APP_BUGSNAG,
    plugins: [new BugsnagPluginVue()],
    releaseStage: process.env.NODE_ENV
});
Bugsnag.getPlugin('vue')
    .installVueErrorHandler(Vue);

// Vue.prototype.$http = ServiceContainer.HTTP.client;
// user security variable
const mgr = new Mgr();

// use axios interceptor to include auth header on all calls
apiService.interceptors.request.use(
    async (config) => {
        // extract token from store
        const token = await mgr.getAccessToken();
        // verify token exists
        if (token) {
            // set authorization header to use token
            config.headers.Authorization = `Bearer ${token}`;

            // extract id token from store
            const idToken = await mgr.getIdToken();
            // set the id token
            config.headers.id_token = idToken;
        }
        // return the axios config
        return config;
    },
    (error) => {
        // return error
        Promise.reject(error);
    }
);

// use axios response interceptor to check if new token should be requested
apiService.interceptors.response.use(
    // return response if successful
    (response) => response,
    (error) => {
        // store the original ajax request
        const originalRequest = error.config;
        // verify axios request was unauthorized
        if (typeof error === 'undefined') {
            // return Error object without Promise
            return Promise.reject();
        } if ('response' in error && typeof error.response !== 'undefined') {
            if ('status' in error.response) {
                if (error.response.status === 401 && !originalRequest._retry) {
                    // set retry
                    originalRequest._retry = true;
                    // renew access token
                    mgr.renewToken();
                    // return originalRequest object with Axios.
                    return axios(originalRequest);
                }
                // return Error object with Promise
                return Promise.reject(error);
            }
            // return Error object with Promise
            return Promise.reject(error);
        }
        // return Error object without Promise
        return Promise.reject();
    }
);

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
    return originalPush.call(this, location).catch((err) => err);
};

require('vue-moment');

Vue.prototype.$http = apiService;

// plugin setup
Vue.mixin(mixins);
Vue.use(VueRouter);
Vue.use(VueRouterPrefetch);
Vue.use(GlobalDirectives);
Vue.use(GlobalComponents);
Vue.use(VueNotify);
Vue.use(SideBar, { sidebarLinks });
Vue.use(VueMoment);
Vue.component('v-select', vSelect);

Vue.use(ElementUI, { locale });

Vue.use(VeeValidate, {
    fieldsBagName: 'vvFields'
});

// const store = makeStore(ServiceContainer);

// configure router
const router = new VueRouter({
    mode: 'history',
    base: '/',
    routes, // short for routes: routes
    linkActiveClass: 'active',
    scrollBehavior: (to) => {
        if (to.hash) {
            return { selector: to.hash };
        }
        return { x: 0, y: 0 };
    }
});

// Configuration VueAnalytics
Vue.use(VueAnalytics, {
    id: 'UA-2678081-41',
    router
});

router.beforeEach((to, from, next) => {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    if (requiresAuth) {
        mgr.getSignedIn().then(
            (isSignedIn) => {
                if (isSignedIn) {
                    // Get the userLocations if they're not in the store
                    if (store.state.userLocations === null) {
                        store.dispatch('getUserLocations').then(() => next());
                    }
                    // Get the role if one is not in the store
                    if (store.state.role === null) {
                        store.dispatch('getRole')
                            .then(() => next())
                            .catch((error) => {
                                if (error.response.status === 404) {
                                    next('/no-account');
                                } else if (error.response.status === 400) {
                                    next('/expired-token');
                                }
                            });
                    } else { next(); }
                } else {
                    next('/login');
                }
            },
            (err) => {
                // console.log(err);
            }
        );
    } else {
        next();
    }
});

initProgress(router);

/* eslint-disable no-new */
new Vue({
    el: '#app',
    store,
    render: (h) => h(App),
    router,
    provide: ServiceContainer
});
